import { useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

/**
 * WebSocket hook for real-time task sync.
 * - Connects to /api/ws?token=<access_token> when authed.
 * - Reconnects with exponential backoff on disconnect.
 * - Calls onMessage with parsed JSON events.
 * Returns { status: 'connecting' | 'open' | 'closed', lastEvent }.
 */
export function useRealtime(onMessage) {
  const { user } = useAuth();
  const [status, setStatus] = useState("connecting");
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!user) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setStatus("closed");
      return;
    }

    let cancelled = false;
    let pingTimer = null;

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem("flowtasks_access_token") || "";
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = base.replace(/^http/, "ws");
      const url = `${wsBase}/api/ws?token=${encodeURIComponent(token)}`;
      setStatus("connecting");
      let ws;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        scheduleRetry();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        setStatus("open");
        pingTimer = setInterval(() => {
          try { ws.send("ping"); } catch {}
        }, 25000);
      };
      ws.onmessage = (ev) => {
        try {
          if (ev.data === "pong") return;
          const data = JSON.parse(ev.data);
          setLastEvent(data);
          onMessageRef.current?.(data);
          // Broadcast globally so non-context subscribers (e.g. GeofenceController) can react.
          try {
            window.dispatchEvent(
              new CustomEvent("flowtasks:ws-message", { detail: data })
            );
          } catch {}
        } catch {}
      };
      ws.onerror = () => {};
      ws.onclose = (ev) => {
        if (pingTimer) clearInterval(pingTimer);
        setStatus("closed");
        wsRef.current = null;
        // 4401 = auth fail. Try refreshing the token, then retry.
        if (ev?.code === 4401) {
          api.post("/auth/refresh").finally(scheduleRetry);
        } else {
          scheduleRetry();
        }
      };
    };

    const scheduleRetry = () => {
      if (cancelled) return;
      retryRef.current = Math.min(retryRef.current + 1, 6);
      const delay = Math.min(1000 * 2 ** retryRef.current, 30000);
      setTimeout(connect, delay);
    };

    connect();
    return () => {
      cancelled = true;
      if (pingTimer) clearInterval(pingTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, [user]);

  return { status, lastEvent };
}
